import React, { useState } from 'react'
import {
  Box,
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import PageHeader from '../components/system/PageHeader'
import TaskTierSection from '../components/theme-ai-tasks/TaskTierList'
import NewTaskDialog from '../components/theme-ai-tasks/NewTaskDialog'
import type { UploadSelection } from '../components/theme-ai-tasks/UploadFileStep'
import { reuseSetupFrom } from '../components/theme-ai-tasks/reuseSetup'
import useAccount from '../hooks/useAccount'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useRouter from '../hooks/useRouter'
import useThemeAITasks from '../hooks/useThemeAITasks'
import { ThemeAIDataSource, ThemeAITask, ThemeAITaskStatus } from '../types/gotypes'
import { themeAIWriteDecision } from '../utils/themeAIOwnership'

// Page -> Hook -> Component. All networking lives in useThemeAITasks; this
// page only owns UI state (filters, dialog open) and coordinates the tier
// list + creation dialog (design spec #916 §3).
const ThemeAITasksPage: React.FC = () => {
  const account = useAccount()
  const router = useRouter()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''
  const whoToAsk = useWhoToAsk(customer)

  const [statusFilter, setStatusFilter] = useState<ThemeAITaskStatus | 'all'>('all')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  // The task whose set-up the New task dialog reuses, if any (#1492 item 9).
  const [reuseSource, setReuseSource] = useState<ThemeAITask>()

  const {
    tasks,
    tasksLoading,
    tasksError,
    createTask,
    createTaskLoading,
    uploadTaskData,
    uploadTaskDataLoading,
    inspectUpload,
    archiveTask,
    promoteTask,
    demoteTask,
  } = useThemeAITasks({
    customer,
    job,
    filters: {
      status: statusFilter === 'all' ? undefined : statusFilter,
      includeArchived,
    },
  })

  // A Manager may star a task to or from Company-level (#1523 item 2), the
  // same idiom as the code frame library and Data Viz.
  const canPromote = Boolean(account.user?.role_permissions?.isManager)

  // Archive is offered on every tier and refused, with the reason, where the
  // server's canModifyThemeAITask would refuse it. A copy does not help with
  // archiving, so the reason offers none (Reuse stays on the row).
  const viewer = { email: account.user?.session_info?.email, isManager: canPromote }
  const canModify = (task: ThemeAITask) =>
    themeAIWriteDecision(task, viewer, `You can't ${task.status === 'archived' ? 'unarchive' : 'archive'} this task.`, whoToAsk)

  // Client-side text filter — tiers are already small, pre-grouped lists;
  // no need to round-trip the server per keystroke.
  const search = searchText.trim().toLowerCase()
  const matchesSearch = (task: ThemeAITask) => !search || task.name.toLowerCase().includes(search)

  const handleOpenTask = (task: ThemeAITask) => {
    router.navigate('theme-ai-task', { customer, job, taskId: task.id })
  }

  const handlePromoteTask = (task: ThemeAITask) => {
    promoteTask(task.id)
  }

  const handleDemoteTask = (task: ThemeAITask) => {
    demoteTask(task.id)
  }

  const handleToggleArchive = (task: ThemeAITask) => {
    archiveTask(task.id, task.status !== 'archived')
  }

  const handleReuseTask = (task: ThemeAITask) => {
    setReuseSource(task)
    setNewTaskOpen(true)
  }

  const handleOpenNewTask = () => {
    // A plain New task never carries a set-up, even when a reuse dialog is
    // still fading out and has not cleared its source yet.
    setReuseSource(undefined)
    setNewTaskOpen(true)
  }

  // Closing keeps the source until the dialog has faded out. The dialog is
  // keyed on the source, so clearing it here would remount the dialog
  // mid-close and cut the close transition.
  const handleCloseNewTask = () => {
    setNewTaskOpen(false)
  }

  const handleNewTaskExited = () => {
    setReuseSource(undefined)
  }

  // The task is created as soon as its data is chosen (spec #916 §2.3). If
  // the upload then fails, the task still exists, so open it on its Data
  // stage — the error is already on screen and Replace data retries it —
  // rather than holding a half-made draft in the dialog.
  const handleCreateTask = async (name: string, dataSource: ThemeAIDataSource, upload?: UploadSelection) => {
    let created: ThemeAITask
    try {
      created = await createTask({ name, data_source: dataSource, ...(reuseSource ? reuseSetupFrom(reuseSource) : {}) })
    } catch {
      return
    }
    if (upload) {
      try {
        await uploadTaskData(created.id, upload.file, upload.columns)
      } catch {
        // Reported by the hook; fall through to the task's Data stage.
      }
    }
    handleCloseNewTask()
    router.navigate('theme-ai-task', { customer, job, taskId: created.id, stage: 'data' })
  }

  return (
    <Box>
      <PageHeader
        title="Theme AI Tasks"
        subtitle="Thematic coding of open-text data — themes, sentiment and emotion."
      />

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* The one thing most visits start with, where the eye lands first (Matt, 21 Sep: "on this side … nice and big and blue"). */}
          <Button variant="contained" color="info" size="large" startIcon={<AddIcon />} onClick={handleOpenNewTask}>
            New task
          </Button>
          <TextField
            size="small"
            placeholder="Search tasks…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            inputProps={{ 'aria-label': 'Search tasks' }}
          />
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ThemeAITaskStatus | 'all')}
            sx={{ minWidth: 140 }}
            inputProps={{ 'aria-label': 'Filter by status' }}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
          <FormControlLabel
            control={<Checkbox size="small" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
            label="Include archived"
          />
        </Box>

        {tasksLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : tasksError ? (
          <Alert severity="error">Theme AI tasks could not be loaded.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Named as in Data Viz (Matt, 23 Sep: "instead of it being called
                exec tasks, it's called the same as in data viz. So company
                level, my tasks, my colleagues' tasks"). */}
            <TaskTierSection
              title="Company-level"
              tone="primary"
              tasks={tasks.exec.filter(matchesSearch)}
              canModify={canModify}
              emptyLabel="No company-level tasks yet."
              onOpenTask={handleOpenTask}
              onToggleArchive={handleToggleArchive}
              onReuse={handleReuseTask}
              canPromote={canPromote}
              onPromoteTask={handlePromoteTask}
              onDemoteTask={handleDemoteTask}
            />
            <TaskTierSection
              title="My tasks"
              tone="info"
              tasks={tasks.mine.filter(matchesSearch)}
              canModify={canModify}
              emptyLabel="You haven't created any Theme AI tasks yet."
              onOpenTask={handleOpenTask}
              onToggleArchive={handleToggleArchive}
              onReuse={handleReuseTask}
              canPromote={canPromote}
              onPromoteTask={handlePromoteTask}
              onDemoteTask={handleDemoteTask}
            />
            <TaskTierSection
              title="My colleagues' tasks"
              tone="secondary"
              tasks={tasks.colleagues.filter(matchesSearch)}
              canModify={canModify}
              emptyLabel="No colleagues' tasks yet."
              onOpenTask={handleOpenTask}
              onToggleArchive={handleToggleArchive}
              onReuse={handleReuseTask}
              canPromote={canPromote}
              onPromoteTask={handlePromoteTask}
              onDemoteTask={handleDemoteTask}
            />
          </Box>
        )}
      </Box>

      <NewTaskDialog
        key={reuseSource?.id ?? 'new'}
        open={newTaskOpen}
        reuseFrom={reuseSource}
        customer={customer}
        job={job}
        creating={createTaskLoading || uploadTaskDataLoading}
        onClose={handleCloseNewTask}
        onExited={handleNewTaskExited}
        onInspectUpload={inspectUpload}
        onCreate={handleCreateTask}
      />
    </Box>
  )
}

export default ThemeAITasksPage
